import { useNavigate } from "react-router-dom";
import CrudTable from "../../../components/crud-table";
import { TeamWithStats } from "../../../types/team.types";
import {
  TeamCompetitionStatus,
  TeamCompetitionStatusColors,
  TeamCompetitionStatusTranslations,
} from "../../../types/team_competitions.types";
import { useTableControls } from "../../../hooks/useTableControls";
import {
  TeamCategoryId,
  TeamCategoryTranslations,
} from "../../../types/team_categories.types";
import Label from "../../../components/label";

export type CompetitionTeamsProps = {
  teams: TeamWithStats[];
};

export function CompetitionTeams({ teams }: CompetitionTeamsProps) {
  const navigate = useNavigate();

  const {
    paginatedData: paginatedTeams,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
  } = useTableControls<TeamWithStats>(teams || []);

  const columns = [
    {
      key: "registration_number" as keyof TeamWithStats,
      label: "N° Reg.",
      sortable: true,
    },
    {
      key: "team_number" as keyof TeamWithStats,
      label: "N° de equipo",
      render: (value: number) => value || "-",
      sortable: true,
    },
    {
      key: "team_name" as keyof TeamWithStats,
      label: "Nombre",
      sortable: true,
    },
    {
      key: "institution_name" as keyof TeamWithStats,
      label: "Institución",
      sortable: true,
    },
    {
      key: "owner_name" as keyof TeamWithStats,
      label: "Dueño",
      render: (value: string) => value || "-",
      sortable: true,
    },
    {
      key: "total_members" as keyof TeamWithStats,
      label: "N° Miembros",
      sortable: true,
      render: (value: number) => value || "-",
    },

    {
      key: "categories" as keyof TeamWithStats,
      label: "Categorías",
      render: (value: TeamWithStats["categories"]) => (
        <div className="flex flex-wrap gap-1">
          {value.length === 0
            ? "-"
            : value.map((cat) => (
                <Label color="yellow" key={cat.category_id}>
                  {TeamCategoryTranslations[cat.category_id as TeamCategoryId]}
                </Label>
              ))}
        </div>
      ),
    },
    {
      key: "competition_status" as keyof TeamWithStats,
      label: "Estado",
      render: (value: TeamCompetitionStatus) => (
        <Label
          color={TeamCompetitionStatusColors[value as TeamCompetitionStatus]}
        >
          {TeamCompetitionStatusTranslations[value as TeamCompetitionStatus] ||
            value}
        </Label>
      ),
      sortable: true,
    },
    {
      key: "is_payment_exempt" as keyof TeamWithStats,
      label: "Exento de pago",
      sortable: true,
      render: (value: boolean) => (
        <Label color={value ? "green" : "gray"}>{value ? "Sí" : "No"}</Label>
      ),
    },

    {
      key: "total_paid_amount" as keyof TeamWithStats,
      label: "Total pagado",
      sortable: true,
      render: (value: number) => `$${value}`,
    },
  ];

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof TeamWithStats | null,
      sortDirection,
    });
  };
  const handleFilterChange = (selectedFilters: Record<string, string[]>) => {
    setSelectedFilters(selectedFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="mt-4">
      <CrudTable
        data={paginatedTeams}
        columns={columns}
        onAdd={{
          onClick: () => navigate("/dashboard/teams/new"),
          label: "Crear equipo",
        }}
        actions={{
          view: {
            label: "",
            onClick: (row: TeamWithStats) =>
              navigate(`/dashboard/teams/view/${row.team_id}`),
          },
        }}
        search={{
          placeholder: "Buscar equipos...",
          onSearch: handleSearch,
        }}
        sort={{
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
          onSort: handleSort,
        }}
        pagination={{
          page: currentPage,
          totalPages: totalPages,
          onPageChange: handlePageChange,
        }}
        filters={[
          {
            key: "competition_status",
            label: "Estado",
            multiple: true,
            options: Object.entries(TeamCompetitionStatusTranslations).map(
              ([statusId, statusName]) => ({
                key: String(statusId),
                value: statusName,
              })
            ),
          },
          {
            key: "categories",
            label: "Categorías",
            multiple: true,
            options: Object.entries(TeamCategoryTranslations).map(
              ([categoryId, categoryName]) => ({
                key: String(categoryId),
                value: categoryName,
              })
            ),
          },
          {
            key: "is_payment_exempt",
            label: "Exento de pago",
            multiple: false,
            options: [
              { key: 1, value: "Sí" },
              { key: 0, value: "No" },
            ],
          }
        ]}
        onFilterChange={handleFilterChange}
        totalCount={filteredData.length}
      />
    </div>
  );
}
